import React, { useEffect, useId, useMemo, useState } from 'react';
import './support.css';
import '../assets/assets.css';
import '../vulnerabilities/vulnerabilities.css';
import '../ghost-cve/ghost-cve.css';
import '../scans/scans.css';
import '../settings/profile/profile.css';
import {
  createSupportTicket,
  replySupportTicket,
  getSupportTicket,
  getSupportTickets,
  updateSupportTicket,
} from '../../services/support.service';
import {
  SUPPORT_TICKET_TABLE_COLUMNS,
  SUPPORT_TICKET_TABLE_COLUMN_WIDTHS_KEY,
} from '../../utils/resizableTableColumns';
import DeploymentInventoryTable from '../settings/deployment/DeploymentInventoryTable';
import BlueXLoader, { PageDataLoader } from '../../components/BlueXLoader';

const CATEGORY_OPTIONS = [
  {
    value: 'account_issue',
    label: 'Account Issue',
    description: 'Login, access, roles, or organisation settings',
  },
  {
    value: 'dashboard_problem',
    label: 'Dashboard Problem',
    description: 'UI errors, missing data, or navigation issues',
  },
  {
    value: 'report_issue',
    label: 'Report Issue',
    description: 'Exports, scheduling, or report content problems',
  },
  {
    value: 'scanner_issue',
    label: 'Scanner Issue',
    description: 'Scan failures, connectors, or scheduling',
  },
  {
    value: 'feature_request',
    label: 'Feature Request',
    description: 'Suggest improvements or new capabilities',
  },
  {
    value: 'general_support',
    label: 'General Support',
    description: 'Anything else you need help with',
  },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', hint: 'Question or minor issue' },
  { value: 'medium', label: 'Medium', hint: 'Affects work but has a workaround' },
  { value: 'high', label: 'High', hint: 'Blocking important tasks' },
  { value: 'urgent', label: 'Urgent', hint: 'Production or security impact' },
];

const MAX_SUPPORT_ATTACHMENTS = 5;
const MAX_SUPPORT_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const SUPPORT_ATTACHMENT_ACCEPT =
  '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.log,.json,.zip,image/*,text/plain,application/pdf';

function formatFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Could not read ${file?.name || 'file'}.`));
    reader.readAsDataURL(file);
  });
}

function resolveSupportWorkspaceRole({ userProfile, uiAccess }) {
  const isSuperuser = userProfile?.is_superuser === true || uiAccess?.isSuperuser === true;
  if (isSuperuser) return 'SUPER_ADMIN';

  const rawRole = String(userProfile?.role || uiAccess?.role || '').trim().toLowerCase();
  const orgId = String(userProfile?.organization_id || '').trim();

  if (rawRole === 'partner') return 'PARTNER';
  if (rawRole === 'staff' || rawRole === 'analyst') return 'STAFF';
  if (rawRole === 'admin' && orgId === '1') return 'ADMIN';
  if (rawRole === 'admin' || rawRole === 'customer_admin') return 'CUSTOMER_ADMIN';
  return 'CUSTOMER_USER';
}

function formatLabel(value) {
  return String(value || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function SupportPage({ userProfile = null, uiAccess = null }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeTab, setActiveTab] = useState('tickets');
  const [ticketSearch, setTicketSearch] = useState('');

  const [formSubject, setFormSubject] = useState('');
  const [formCategory, setFormCategory] = useState('general_support');
  const [formPriority, setFormPriority] = useState('medium');
  const [formDescription, setFormDescription] = useState('');
  const [formAttachments, setFormAttachments] = useState([]);
  const [formMessage, setFormMessage] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);

  const [replyText, setReplyText] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [priorityValue, setPriorityValue] = useState('');

  const ticketSearchId = useId();

  const selectedTicketNumber = selectedTicket?.ticket_number || '';
  const workspaceRole = useMemo(
    () => resolveSupportWorkspaceRole({ userProfile, uiAccess }),
    [userProfile, uiAccess]
  );
  const canManageState =
    workspaceRole === 'SUPER_ADMIN' ||
    workspaceRole === 'ADMIN' ||
    workspaceRole === 'PARTNER' ||
    workspaceRole === 'STAFF';

  const statusOptions = useMemo(
    () => [
      { value: 'open', label: 'Open' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'pending_customer', label: 'Pending Customer' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' },
    ],
    []
  );

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const ticketRows = await getSupportTickets();
      const safeTickets = Array.isArray(ticketRows) ? ticketRows : [];
      setTickets(safeTickets);

      if (!selectedTicketId && safeTickets.length > 0) {
        setSelectedTicketId(safeTickets[0].id);
      }
    } catch (err) {
      setError(err?.message || 'Support content is currently unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const onTab = (event) => {
      const tab = String(event?.detail?.tab || '').trim();
      if (tab === 'tickets' || tab === 'create') setActiveTab(tab);
    };
    const onSelectFirst = () => {
      setActiveTab('tickets');
      setSelectedTicketId((current) => {
        if (current) return current;
        const firstId = tickets[0]?.id;
        return firstId || current;
      });
    };
    window.addEventListener('xspectre:tour-support-tab', onTab);
    window.addEventListener('xspectre:tour-select-first-ticket', onSelectFirst);
    return () => {
      window.removeEventListener('xspectre:tour-support-tab', onTab);
      window.removeEventListener('xspectre:tour-select-first-ticket', onSelectFirst);
    };
  }, [tickets]);

  useEffect(() => {
    let cancelled = false;
    const loadTicketDetail = async () => {
      if (!selectedTicketId) {
        setSelectedTicket(null);
        return;
      }
      try {
        const detail = await getSupportTicket(selectedTicketId);
        if (!cancelled) {
          setSelectedTicket(detail || null);
          setStatusValue(detail?.status || '');
          setPriorityValue(detail?.priority || '');
        }
      } catch (err) {
        if (!cancelled) {
          setSelectedTicket(null);
          setError(err?.message || 'Failed to load ticket detail.');
        }
      }
    };

    loadTicketDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedTicketId]);

  const filteredTickets = useMemo(() => {
    const query = String(ticketSearch || '').trim().toLowerCase();
    if (!query) return tickets;
    return tickets.filter((ticket) =>
      [
        ticket.ticket_number,
        ticket.subject,
        ticket.status,
        ticket.priority,
        ticket.created_at,
      ]
        .map((part) => String(part ?? '').toLowerCase())
        .join(' ')
        .includes(query)
    );
  }, [tickets, ticketSearch]);

  const addAttachmentFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter(Boolean);
    if (!incoming.length) return;

    setAttachmentError('');
    setAttachmentsBusy(true);
    try {
      const next = [...formAttachments];
      for (const file of incoming) {
        if (next.length >= MAX_SUPPORT_ATTACHMENTS) {
          setAttachmentError(`You can attach up to ${MAX_SUPPORT_ATTACHMENTS} files.`);
          break;
        }
        if (!file.size || file.size > MAX_SUPPORT_ATTACHMENT_BYTES) {
          setAttachmentError(
            `"${file.name}" must be between 1 byte and ${formatFileSize(MAX_SUPPORT_ATTACHMENT_BYTES)}.`
          );
          continue;
        }
        const duplicate = next.some(
          (item) => item.name === file.name && item.size === file.size && item.content_type === (file.type || 'application/octet-stream')
        );
        if (duplicate) continue;

        const url = await readFileAsDataUrl(file);
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified || Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          content_type: file.type || 'application/octet-stream',
          size: file.size,
          url,
        });
      }
      setFormAttachments(next);
    } catch (err) {
      setAttachmentError(err?.message || 'Failed to read one or more files.');
    } finally {
      setAttachmentsBusy(false);
    }
  };

  const removeAttachment = (attachmentId) => {
    setFormAttachments((current) => current.filter((item) => item.id !== attachmentId));
    setAttachmentError('');
  };

  const resetCreateForm = () => {
    setFormSubject('');
    setFormCategory('general_support');
    setFormPriority('medium');
    setFormDescription('');
    setFormAttachments([]);
    setAttachmentError('');
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    if (formSubmitting || attachmentsBusy) return;
    setFormMessage('');
    setError('');
    setFormSubmitting(true);

    try {
      const payload = {
        subject: formSubject,
        category: formCategory,
        description: formDescription,
        priority: formPriority,
        attachments: formAttachments.map(({ name, content_type, size, url }) => ({
          name,
          content_type,
          size,
          url,
        })),
      };

      const result = await createSupportTicket(payload);
      const createdId = result?.ticket?.id;
      setFormMessage(`Ticket ${result?.ticketNumber || ''} created successfully.`.trim());

      resetCreateForm();

      await loadAll();
      if (createdId) {
        setSelectedTicketId(createdId);
        setActiveTab('tickets');
      }
    } catch (err) {
      setError(err?.message || 'Failed to create support ticket.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!selectedTicketId || !replyText.trim()) return;
    try {
      await replySupportTicket(selectedTicketId, { reply: replyText.trim() });
      setReplyText('');
      const detail = await getSupportTicket(selectedTicketId);
      setSelectedTicket(detail || null);
      await loadAll();
    } catch (err) {
      setError(err?.message || 'Failed to add reply.');
    }
  };

  const visibleHistory = useMemo(() => {
    const rows = Array.isArray(selectedTicket?.history) ? selectedTicket.history : [];
    if (canManageState) return rows;
    return rows.filter((event) => String(event?.event_type || '').toLowerCase() !== 'internal_note');
  }, [selectedTicket, canManageState]);

  const handleUpdateStatusPriority = async () => {
    if (!selectedTicketId) return;
    try {
      await updateSupportTicket(selectedTicketId, {
        status: statusValue,
        priority: priorityValue,
      });
      const detail = await getSupportTicket(selectedTicketId);
      setSelectedTicket(detail || null);
      await loadAll();
    } catch (err) {
      setError(err?.message || 'Status or priority update was not allowed.');
    }
  };

  if (loading) {
    return (
      <section className="assets-section support-page" data-tour="support-page" aria-label="Support">
        <PageDataLoader label="Loading support" />
      </section>
    );
  }

  return (
    <section className="assets-section support-page" data-tour="support-page" aria-label="Support">
      <div className="support-page-messages">
        {loading ? (
          <div className="support-loading">
            <BlueXLoader />
          </div>
        ) : null}
        {error ? (
          <div className="profile-settings-message profile-settings-error" role="alert">
            {error}
          </div>
        ) : null}
        {formMessage ? (
          <div className="profile-settings-message profile-settings-success" role="status">
            {formMessage}
          </div>
        ) : null}
      </div>

      <div className="support-page-layout">
        <div className="support-page-main">
          <div
            className="scans-network-scope-tabs support-view-tabs"
            data-tour="support-tabs"
            role="tablist"
            aria-label="Support views"
          >
            <div className="scans-network-scope-tabs-track support-view-tabs-track">
              <button
                type="button"
                role="tab"
                id="support-tab-tickets"
                aria-selected={activeTab === 'tickets'}
                aria-controls="support-panel-tickets"
                className={`scans-network-scope-tab${activeTab === 'tickets' ? ' is-active' : ''}`}
                data-tour="support-tab-tickets"
                onClick={() => setActiveTab('tickets')}
              >
                <span className="scans-network-scope-tab-main">
                  <span className="scans-network-scope-tab-label">My Tickets</span>
                </span>
              </button>
              <button
                type="button"
                role="tab"
                id="support-tab-create"
                aria-selected={activeTab === 'create'}
                aria-controls="support-panel-create"
                className={`scans-network-scope-tab${activeTab === 'create' ? ' is-active' : ''}`}
                data-tour="support-tab-create"
                onClick={() => setActiveTab('create')}
              >
                <span className="scans-network-scope-tab-main">
                  <span className="scans-network-scope-tab-label">New Request</span>
                </span>
              </button>
            </div>
          </div>

          {activeTab === 'create' ? (
            <form
              id="support-panel-create"
              className="support-create-form"
              data-tour="support-create-form"
              role="tabpanel"
              aria-labelledby="support-tab-create"
              onSubmit={handleCreateTicket}
              noValidate
            >
              <header className="support-create-header">
                <div>
                  <h3 className="support-create-title">Create Support Request</h3>
                  <p className="support-create-lead">
                    Choose a category, describe the issue, and our team will follow up by email.
                  </p>
                </div>
              </header>

              <div className="support-create-body">
                <section
                  className="support-create-section"
                  data-tour="support-create-category"
                  aria-labelledby="support-create-category-heading"
                >
                  <div className="support-create-section-head">
                    <h4 id="support-create-category-heading" className="support-create-section-title">
                      Category
                    </h4>
                    <p className="support-create-section-hint">Select the area that best matches your request.</p>
                  </div>
                  <div className="support-category-grid" role="radiogroup" aria-label="Support category">
                    {CATEGORY_OPTIONS.map((option) => {
                      const selected = formCategory === option.value;
                      return (
                        <label
                          key={option.value}
                          className={`support-category-card${selected ? ' is-selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="support-category"
                            className="support-category-input"
                            value={option.value}
                            checked={selected}
                            onChange={() => setFormCategory(option.value)}
                          />
                          <span className="support-category-card-label">{option.label}</span>
                          <span className="support-category-card-desc">{option.description}</span>
                        </label>
                      );
                    })}
                  </div>
                </section>

                <section
                  className="support-create-section"
                  data-tour="support-create-details"
                  aria-labelledby="support-create-details-heading"
                >
                  <div className="support-create-section-head">
                    <h4 id="support-create-details-heading" className="support-create-section-title">
                      Request details
                    </h4>
                  </div>
                  <div className="support-create-fields">
                    <div className="support-create-field support-create-field--full">
                      <label className="support-create-label" htmlFor="support-form-subject">
                        Subject
                      </label>
                      <input
                        id="support-form-subject"
                        className="support-control ghost-exposure-sys-input"
                        value={formSubject}
                        onChange={(event) => setFormSubject(event.target.value)}
                        placeholder="Brief summary of your issue"
                        required
                      />
                    </div>

                    <div className="support-create-field support-create-field--full">
                      <span className="support-create-label" id="support-form-priority-label">
                        Priority
                      </span>
                      <div
                        className="support-priority-group"
                        role="radiogroup"
                        aria-labelledby="support-form-priority-label"
                      >
                        {PRIORITY_OPTIONS.map((option) => {
                          const selected = formPriority === option.value;
                          return (
                            <label
                              key={option.value}
                              className={`support-priority-option support-priority-option--${option.value}${
                                selected ? ' is-selected' : ''
                              }`}
                            >
                              <input
                                type="radio"
                                name="support-priority"
                                className="support-priority-input"
                                value={option.value}
                                checked={selected}
                                onChange={() => setFormPriority(option.value)}
                              />
                              <span className="support-priority-label">{option.label}</span>
                              <span className="support-priority-hint">{option.hint}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="support-create-field support-create-field--full">
                      <label className="support-create-label" htmlFor="support-form-description">
                        Description
                      </label>
                      <textarea
                        id="support-form-description"
                        className="support-control support-textarea ghost-exposure-sys-input"
                        value={formDescription}
                        onChange={(event) => setFormDescription(event.target.value)}
                        placeholder="What happened, what you expected, and any steps to reproduce."
                        required
                      />
                      <p className="support-field-footnote">
                        Include scan IDs, asset names, or screenshots if relevant.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="support-create-section support-create-attachments" data-tour="support-create-attachments">
                  <div className="support-attach-heading">
                    <label className="support-create-label" htmlFor="support-file-input">
                      Attachments (optional)
                    </label>
                    <p className="support-field-footnote">
                      Up to {MAX_SUPPORT_ATTACHMENTS} files · {formatFileSize(MAX_SUPPORT_ATTACHMENT_BYTES)} each · images, PDF, text, CSV, ZIP
                    </p>
                  </div>

                  <label
                    className={`support-dropzone${attachmentsBusy ? ' is-busy' : ''}`}
                    htmlFor="support-file-input"
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (formSubmitting || attachmentsBusy) return;
                      addAttachmentFiles(event.dataTransfer?.files);
                    }}
                  >
                    <input
                      id="support-file-input"
                      className="support-file-input"
                      type="file"
                      multiple
                      accept={SUPPORT_ATTACHMENT_ACCEPT}
                      disabled={formSubmitting || attachmentsBusy || formAttachments.length >= MAX_SUPPORT_ATTACHMENTS}
                      onChange={(event) => {
                        addAttachmentFiles(event.target.files);
                        event.target.value = '';
                      }}
                    />
                    <span className="support-dropzone-title">
                      Choose files or drag them here
                    </span>
                    <span className="support-dropzone-hint">
                      Screenshots, logs, and exports help us investigate faster.
                    </span>
                  </label>

                  {attachmentError ? (
                    <p className="support-attach-error" role="alert">{attachmentError}</p>
                  ) : null}

                  {formAttachments.length > 0 ? (
                    <ul className="support-attach-list">
                      {formAttachments.map((file) => (
                        <li key={file.id} className="support-attach-item">
                          <div className="support-attach-meta">
                            <strong className="support-attach-name" title={file.name}>{file.name}</strong>
                            <span className="support-attach-size">{formatFileSize(file.size)}</span>
                          </div>
                          <button
                            type="button"
                            className="support-attach-remove"
                            onClick={() => removeAttachment(file.id)}
                            disabled={formSubmitting || attachmentsBusy}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              </div>

              <footer className="support-create-footer" data-tour="support-create-submit">
                <p className="support-create-footer-note">
                  You will receive email updates when your ticket status changes.
                </p>
                <div className="support-form-actions">
                  <button type="submit" className="support-primary-btn" disabled={formSubmitting || attachmentsBusy}>
                    Submit request
                  </button>
                </div>
              </footer>
            </form>
          ) : (
            <div
              id="support-panel-tickets"
              className="support-tickets-panel"
              data-tour="support-tickets-panel"
              role="tabpanel"
              aria-labelledby="support-tab-tickets"
            >
              <div className="assets-table-toolbar">
                <p className="connectors-settings-count" aria-live="polite">
                  <span className="connectors-settings-count-value">{filteredTickets.length}</span>{' '}
                  {filteredTickets.length === 1 ? 'ticket' : 'tickets'}
                  {ticketSearch.trim() ? ' match your search' : ''}
                </p>
                <div className="subscription-org-management-search" data-tour="support-search">
                  <label htmlFor={ticketSearchId} className="edi-sr-only">
                    Search tickets
                  </label>
                  <div className="subscription-org-management-search-inner">
                    <input
                      id={ticketSearchId}
                      type="search"
                      className="subscription-org-management-search-input"
                      value={ticketSearch}
                      onChange={(event) => setTicketSearch(event.target.value)}
                      placeholder="Search tickets"
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {ticketSearch ? (
                      <button
                        type="button"
                        className="ghost-exposure-sys-table-search-clear"
                        onClick={() => setTicketSearch('')}
                        aria-label="Clear ticket search"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div
                className="support-table support-tickets-table-wrap"
                data-tour="support-ticket-list"
              >
                <DeploymentInventoryTable
                  columns={SUPPORT_TICKET_TABLE_COLUMNS}
                  widthsKey={SUPPORT_TICKET_TABLE_COLUMN_WIDTHS_KEY}
                  rows={filteredTickets}
                  emptyLabel={
                    ticketSearch.trim() ? 'No tickets match your search.' : 'No support tickets yet.'
                  }
                  selectedKey={selectedTicketId}
                  getRowKey={(ticket) => ticket.id}
                  onSelectRow={(ticket) => setSelectedTicketId(ticket.id)}
                  renderRow={(ticket) => (
                    <>
                      <td className="assets-cell-ellipsis support-ticket-number" title={ticket.ticket_number}>
                        {ticket.ticket_number}
                      </td>
                      <td className="assets-cell-ellipsis support-ticket-subject" title={ticket.subject}>
                        {ticket.subject}
                      </td>
                      <td>
                        <span className={`support-badge support-badge--status-${ticket.status}`}>
                          {formatLabel(ticket.status)}
                        </span>
                      </td>
                      <td>
                        <span className={`support-badge support-badge--priority-${ticket.priority}`}>
                          {formatLabel(ticket.priority)}
                        </span>
                      </td>
                      <td className="assets-cell-ellipsis support-ticket-created" title={formatDateTime(ticket.created_at)}>
                        {formatDateTime(ticket.created_at)}
                      </td>
                    </>
                  )}
                />
              </div>

              <div className="support-ticket-detail support-panel">
                <div className="support-ticket-detail-top">
                  <h3
                    className="support-panel-title"
                    data-tour="support-ticket-detail"
                    data-filled={selectedTicket ? 'true' : 'false'}
                  >
                    Ticket Details
                  </h3>
                  {selectedTicketNumber ? (
                    <span className="support-ticket-detail-id">{selectedTicketNumber}</span>
                  ) : null}
                </div>
                {!selectedTicket ? (
                  <p className="support-empty-state">Select a ticket to view conversation history.</p>
                ) : (
                  <>
                    <div className="support-ticket-detail-summary">
                      <div className="support-ticket-detail-header">
                        <div className="support-ticket-detail-heading">
                          <h4 className="support-ticket-detail-title">{selectedTicket.subject}</h4>
                          <p className="support-ticket-detail-created">
                            Created {formatDateTime(selectedTicket.created_at)}
                          </p>
                        </div>
                        <div className="support-ticket-detail-meta">
                          <span className={`support-badge support-badge--status-${selectedTicket.status}`}>
                            {formatLabel(selectedTicket.status)}
                          </span>
                          <span className={`support-badge support-badge--priority-${selectedTicket.priority}`}>
                            {formatLabel(selectedTicket.priority)}
                          </span>
                        </div>
                      </div>
                      <div className="support-ticket-detail-section">
                        <h5 className="support-ticket-detail-section-title">Description</h5>
                        <p className="support-ticket-detail-description">{selectedTicket.description}</p>
                      </div>
                    </div>

                    {canManageState ? (
                      <div className="support-ticket-detail-section support-admin-section">
                        <h5 className="support-ticket-detail-section-title">Update State</h5>
                        <div className="support-admin-controls">
                          <label className="support-admin-field">
                            <span className="support-admin-field-label">Status</span>
                            <select
                              className="support-select"
                              value={statusValue}
                              onChange={(event) => setStatusValue(event.target.value)}
                              aria-label="Ticket status"
                            >
                              {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="support-admin-field">
                            <span className="support-admin-field-label">Priority</span>
                            <select
                              className="support-select"
                              value={priorityValue}
                              onChange={(event) => setPriorityValue(event.target.value)}
                              aria-label="Ticket priority"
                            >
                              {PRIORITY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="support-admin-actions">
                            <button
                              type="button"
                              className="support-secondary-btn"
                              onClick={handleUpdateStatusPriority}
                            >
                              Update State
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="support-ticket-detail-section support-reply-block" data-tour="support-ticket-reply">
                      <h5 className="support-ticket-detail-section-title">
                        <label className="support-form-label" htmlFor="support-reply-text">
                          Reply
                        </label>
                      </h5>
                      <textarea
                        id="support-reply-text"
                        className="support-textarea"
                        value={replyText}
                        onChange={(event) => setReplyText(event.target.value)}
                        placeholder="Add a reply for the support team"
                      />
                      <div className="support-form-actions">
                        <button
                          type="button"
                          className="support-primary-btn"
                          onClick={handleReply}
                          disabled={!replyText.trim()}
                        >
                          Post Reply
                        </button>
                      </div>
                    </div>

                    <div className="support-ticket-detail-section" data-tour="support-ticket-timeline">
                      <h5 className="support-ticket-detail-section-title">Activity</h5>
                      <div className="support-timeline">
                        {visibleHistory.length === 0 ? (
                          <p className="support-empty-state">No activity yet.</p>
                        ) : (
                          visibleHistory.map((event) => (
                            <article key={event.id} className="support-timeline-item">
                              <div className="support-timeline-head">
                                <div>
                                  <div className="support-timeline-type">{formatLabel(event.event_type)}</div>
                                  <div className="support-timeline-actor">{event.actor_name}</div>
                                </div>
                                <time className="support-timeline-time" dateTime={event.created_at || undefined}>
                                  {formatDateTime(event.created_at)}
                                </time>
                              </div>
                              <p className="support-timeline-message">{event.message}</p>
                            </article>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default SupportPage;
